import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PhoneNumberInput } from "@/components/rider/quote/PhoneNumberInput";
import { useToast } from "@/hooks/use-toast";

const registerNewSchema = z.object({
  organization_name: z.string().min(1, "Organization name is required"),
  website: z.string().min(1, "Website is required"),
  phone: z.string().min(10, "Phone number must be at least 10 digits"),
  city: z.string().min(1, "City is required"),
});

type RegisterNewFormValues = z.infer<typeof registerNewSchema>;

export const RegisterNewForm = () => {
  const { toast } = useToast();

  const form = useForm<RegisterNewFormValues>({
    resolver: zodResolver(registerNewSchema),
    defaultValues: {
      organization_name: "",
      website: "",
      phone: "",
      city: "",
    },
  });

  const onSubmit = (data: RegisterNewFormValues) => {
    // Store data locally
    localStorage.setItem("register_new_data", JSON.stringify({
      ...data,
      timestamp: new Date().toISOString(),
    }));

    // Compose email
    const subject = encodeURIComponent("NEW Operator Partnership Request");
    const body = encodeURIComponent(
      `Organization Name: ${data.organization_name}\n` +
      `Website: ${data.website}\n` +
      `Phone Number: ${data.phone}\n` +
      `City: ${data.city}\n\n` +
      `Submitted: ${new Date().toLocaleString()}`
    );
    
    const mailtoLink = `mailto:disco@uscoachways.com?subject=${subject}&body=${body}`;
    
    // Open email client
    window.location.href = mailtoLink;

    toast({
      title: "Success",
      description: "Your partnership request has been prepared. Please send the email from your email client.",
    });
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-black mb-2">Partner with Disco</h1>
        <p className="text-gray-600">Join our network of transportation operators</p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="organization_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-black">Organization Name</FormLabel>
                <FormControl>
                  <Input 
                    placeholder="Your Company Name" 
                    {...field} 
                    className="bg-white border-gray-200" 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="website"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-black">Website</FormLabel>
                <FormControl>
                  <Input 
                    placeholder="https://yourcompany.com" 
                    {...field} 
                    className="bg-white border-gray-200" 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <PhoneNumberInput 
                    phoneNumber={field.value} 
                    onChange={(e) => field.onChange(e.target.value)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="city"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-black">What city are you in?</FormLabel>
                <FormControl>
                  <Input 
                    placeholder="City Name" 
                    {...field} 
                    className="bg-white border-gray-200" 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button 
            type="submit" 
            className="w-full gradient-bg text-black font-bold text-lg py-6 rounded-full hover:opacity-95 transition-all"
          >
            Submit Partnership Request
          </Button>
        </form>
      </Form>
    </div>
  );
};
